import { Header, Segment } from '@mairistem/composants';
import { Icon } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import DECISION from '../../constants/common/decision';
import STATUT_PROJET from '../../constants/common/statutProjet';
import type AutorisationCount from '../../interfaces/common/autorisationCount';
import type AutorisationStatutCount from '../../interfaces/common/autorisationStatutCount';
import {
	getAutorisationCount,
	getAutorisationStatutCount,
} from '../../services/projet';
import AutorisationFilters, {
	type AutorisationFiltersData,
} from './AutorisationFilters';
import AutorisationFooter from './AutorisationFooter';
import AutorisationList from './AutorisationList';

interface AutorisationProps {
	decision: number;
}

const defaultAutorisationCount = {
	montant: 0,
	total: 0,
};

const defaultAutorisationStatutCount = {
	brouillon: 0,
	enCours: 0,
	solde: 0,
	toutes: 0,
};

const defaultFilters = {
	statut: STATUT_PROJET.TOUTES,
	q: '',
};

const Autorisation = ({ decision }: AutorisationProps) => {
	const [autorisationCount, setAutorisationCount] =
		React.useState<AutorisationCount>(defaultAutorisationCount);
	const [autorisationStatutCount, setAutorisationStatutCount] =
		React.useState<AutorisationStatutCount>(defaultAutorisationStatutCount);
	const [filters, setFilters] =
		React.useState<AutorisationFiltersData>(defaultFilters);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'decision'
	const fetchAutorisationCount = React.useCallback(async () => {
		try {
			const data = await getAutorisationCount(
				new URLSearchParams({
					...(filters.statut !== null &&
						filters.statut !== STATUT_PROJET.TOUTES && {
							statut: filters.statut.toString(),
						}),
					...(filters.q && { q: filters.q }),
					decision: String(decision),
				}),
			);
			setAutorisationCount(data);
		} catch (_e) {
			toast.error('Erreur lors de la récupération des compteurs de la liste');
		}
	}, [filters]);

	const fetchAutorisationStatutCount = async () => {
		try {
			const data = await getAutorisationStatutCount(
				new URLSearchParams({ decision: String(decision) }),
			);
			setAutorisationStatutCount({
				...data,
				toutes: data.brouillon + data.enCours + data.solde,
			});
		} catch (_e) {
			toast.error('Erreur lors de la récupération des compteurs de statut');
		}
	};

	const handleFilters = (newFilters: AutorisationFiltersData) => {
		setFilters((old) => ({ ...old, ...newFilters }));
	};

	React.useEffect(() => {
		fetchAutorisationCount();
	}, [fetchAutorisationCount]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'fetchAutorisationStatutCount'
	React.useEffect(() => {
		fetchAutorisationStatutCount();
	}, []);

	return (
		<Segment basic>
			<Segment basic>
				<Header>
					<Icon
						color="var(--accent)"
						iconSet="Lucide"
						name={decision === DECISION.AP ? 'SquareCheckBig' : 'FileText'}
					/>
					<Header.Content>
						{`Autorisations ${decision === DECISION.AP ? 'de programme' : "d'engagement"}`}
						<Header.Subheader
							content={`Gestion des autorisations ${decision === DECISION.AP ? 'de programme' : "d'engagement"} et crédits de paiement (section ${decision === DECISION.AP ? "d'investissement" : 'de fonctionnement'})`}
						/>
					</Header.Content>
				</Header>
				<AutorisationFilters
					autorisationStatutCount={autorisationStatutCount}
					filters={filters}
					onFilters={handleFilters}
				/>
				<AutorisationList decision={decision} filters={filters} />
				<AutorisationFooter
					decision={decision}
					montant={autorisationCount.montant}
					total={autorisationCount.total}
				/>
			</Segment>
		</Segment>
	);
};

export default Autorisation;
